import {
  AdAnalyticsFilter,
  // AdAnalyticsVideo,
  AdAnalyticsVideoFilter,
  AdAnalyticsVideoSort,
  // createSomeAdAnalyticsVideo,
} from '@kaizenplatform/hugot-entity';
import gql from 'graphql-tag';
import { List } from 'immutable';

import GraphQLClient from '../../adapter/GraphQLClient';
import CreativePerformanceBrowsingAd from '../../entity/CreativePerformanceBrowsingAd';
import CreativePerformanceBrowsingCampaign from '../../entity/CreativePerformanceBrowsingCampaign';
import CreativePerformanceBrowsingCreative from '../../entity/CreativePerformanceBrowsingCreative';
import WithTotalCountArrayPageInfo from '../../entity/WithTotalCountArrayPageInfo';

class CreativePerformanceCreativeWebApi {
  private readonly graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  // @TODO platformt, media, placementをarrayにする
  // @TODO analyzeStateとtitleを追加

  public creatives = async ({
    platformKey,
    mediaKey,
    placementKey,
    period,
    perPage,
    after,
    before,
    filters,
    sort,
    organizationId,
  }: AdAnalyticsFilter & {
    filters: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
    organizationId?: string;
  }): Promise<{
    creativePerformanceBrowsingCreatives: List<CreativePerformanceBrowsingCreative>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        query creativePerformanceBrowsingCreatives(
          $adPlatformKeys: [AdPlatformKey!]!
          $adMediaKeys: [AdMediaKey!]!
          $adPlacementKeys: [AdPlacementKey!]!
          $period: PerformanceReportPeriodPattern!
          $first: Int
          $after: String
          $before: String
          $sort: AdAnalyticsVideoSort
          $organizationId: ID!
        ) {
          creativePerformanceBrowsingCreatives(
            adPlatformKeys: $adPlatformKeys
            adMediaKeys: $adMediaKeys
            adPlacementKeys: $adPlacementKeys
            period: $period
            first: $first
            sort: $sort
            organizationId: $organizationId
            after: $after
            before: $before
          ) {
            edges {
              node {
                title
                project {
                  id
                  title
                }
                platforms {
                  key
                  name
                }
                medias {
                  key
                  name
                }
                placements {
                  key
                  name
                }
                spend
                mobileAppInstalls
                impressions
                clicks
                currency
                conversions
                conversionsKey
                viewThroughConversions
                videoP25Watched
                videoP50Watched
                videoP75Watched
                videoP95Watched
                videoP100Watched
                cvr
                ctr
                cpc
                cpm
                cpa
                cpaWithViewThroughConversions
                duration
                thumbnailImageUrl
              }
            }
            pageInfo {
              startCursor
            }
          }
        }
      `,
      {
        adPlatformKeys: platformKey !== undefined ? platformKey : [],
        adMediaKeys: mediaKey !== undefined ? mediaKey : [],
        adPlacementKeys: placementKey !== undefined ? placementKey : [],
        period: period,
        first: perPage,
        after: after,
        before: before,
        filters: filters,
        sort: sort,
        organizationId: organizationId,
      },
    );
    if (!Array.isArray(result.creativePerformanceBrowsingCreatives.edges)) {
      throw new Error('result.creativePerformanceBrowsingCreatives.edges must be an array.');
    }

    return {
      creativePerformanceBrowsingCreatives: List(
        result.creativePerformanceBrowsingCreatives.edges.map((edge: any) =>
          CreativePerformanceBrowsingCreative.fromJSON(edge.node),
        ),
      ),
      pageInfo: result.creativePerformanceBrowsingCreatives.pageInfo as WithTotalCountArrayPageInfo,
    };
  };

  public campaigns = async ({
    platformKey,
    mediaKey,
    placementKey,
    period,
    perPage,
    after,
    before,
    filters,
    sort,
    organizationId,
  }: AdAnalyticsFilter & {
    filters: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
    organizationId?: string;
  }): Promise<{
    creativePerformanceBrowsingCampaigns: List<CreativePerformanceBrowsingCampaign>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        query creativePerformanceBrowsingCampaigns(
          $adPlatformKeys: [AdPlatformKey!]!
          $adMediaKeys: [AdMediaKey!]!
          $adPlacementKeys: [AdPlacementKey!]!
          $period: PerformanceReportPeriodPattern!
          $first: Int
          $after: String
          $before: String
          $sort: AdAnalyticsVideoSort
          $organizationId: ID!
        ) {
          creativePerformanceBrowsingCampaigns(
            adPlatformKeys: $adPlatformKeys
            adMediaKeys: $adMediaKeys
            adPlacementKeys: $adPlacementKeys
            period: $period
            first: $first
            sort: $sort
            organizationId: $organizationId
            after: $after
            before: $before
          ) {
            edges {
              node {
                title
                project {
                  id
                  title
                }
                platforms {
                  key
                  name
                }
                medias {
                  key
                  name
                }
                placements {
                  key
                  name
                }
                spend
                mobileAppInstalls
                impressions
                clicks
                currency
                conversions
                conversionsKey
                viewThroughConversions
                videoP25Watched
                videoP50Watched
                videoP75Watched
                videoP95Watched
                videoP100Watched
                cvr
                ctr
                cpc
                cpm
                cpa
                cpaWithViewThroughConversions
                duration
                thumbnailImageUrl
              }
            }
            pageInfo {
              endCursor
              startCursor
            }
          }
        }
      `,
      {
        adPlatformKeys: platformKey !== undefined ? platformKey : [],
        adMediaKeys: mediaKey !== undefined ? mediaKey : [],
        adPlacementKeys: placementKey !== undefined ? placementKey : [],
        period: period,
        first: perPage,
        after: after,
        before: before,
        filters: filters,
        sort: sort,
        organizationId: organizationId,
      },
    );
    if (!Array.isArray(result.creativePerformanceBrowsingCampaigns.edges)) {
      throw new Error('result.creativePerformanceBrowsingCampaigns.edges must be an array.');
    }

    return {
      creativePerformanceBrowsingCampaigns: List(
        result.creativePerformanceBrowsingCampaigns.edges.map((edge: any) =>
          CreativePerformanceBrowsingCampaign.fromJSON(edge.node),
        ),
      ),
      pageInfo: result.creativePerformanceBrowsingCampaigns.pageInfo as WithTotalCountArrayPageInfo,
    };
  };

  public ads = async ({
    platformKey,
    mediaKey,
    placementKey,
    period,
    perPage,
    after,
    before,
    filters,
    sort,
    organizationId,
  }: AdAnalyticsFilter & {
    filters: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
    organizationId?: string;
  }): Promise<{
    creativePerformanceBrowsingAds: List<CreativePerformanceBrowsingAd>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        query creativePerformanceBrowsingAds(
          $adPlatformKeys: [AdPlatformKey!]!
          $adMediaKeys: [AdMediaKey!]!
          $adPlacementKeys: [AdPlacementKey!]!
          $period: PerformanceReportPeriodPattern!
          $first: Int
          $after: String
          $before: String
          $sort: AdAnalyticsVideoSort
          $organizationId: ID!
        ) {
          creativePerformanceBrowsingAds(
            adPlatformKeys: $adPlatformKeys
            adMediaKeys: $adMediaKeys
            adPlacementKeys: $adPlacementKeys
            period: $period
            first: $first
            sort: $sort
            organizationId: $organizationId
            after: $after
            before: $before
          ) {
            edges {
              node {
                title
                project {
                  id
                  title
                }
                platforms {
                  key
                  name
                }
                medias {
                  key
                  name
                }
                placements {
                  key
                  name
                }
                spend
                mobileAppInstalls
                impressions
                clicks
                currency
                conversions
                conversionsKey
                viewThroughConversions
                videoP25Watched
                videoP50Watched
                videoP75Watched
                videoP95Watched
                videoP100Watched
                cvr
                ctr
                cpc
                cpm
                cpa
                cpaWithViewThroughConversions
                duration
                thumbnailImageUrl
              }
            }
            pageInfo {
              endCursor
              startCursor
            }
          }
        }
      `,
      {
        adPlatformKeys: platformKey !== undefined ? platformKey : [],
        adMediaKeys: mediaKey !== undefined ? mediaKey : [],
        adPlacementKeys: placementKey !== undefined ? placementKey : [],
        period: period,
        first: perPage,
        after: after,
        before: before,
        filters: filters,
        sort: sort,
        organizationId: organizationId,
      },
    );
    if (!Array.isArray(result.creativePerformanceBrowsingAds.edges)) {
      throw new Error('result.creativePerformanceBrowsingAds.edges must be an array.');
    }

    return {
      creativePerformanceBrowsingAds: List(
        result.creativePerformanceBrowsingAds.edges.map((edge: any) =>
          CreativePerformanceBrowsingAd.fromJSON(edge.node),
        ),
      ),
      pageInfo: result.creativePerformanceBrowsingAds.pageInfo as WithTotalCountArrayPageInfo,
    };
  };
}

export default CreativePerformanceCreativeWebApi;
